import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MoreVertical, Download, FileSpreadsheet, ClipboardCopy, Check, Link2 } from "lucide-react";
import { cn } from "../../../../../lib/utils";
import { primaryText, semiBoldText, xxLargeTextSize } from "../../../../../styling/stylingHelper";
import { vatOptions } from "../../../../ui/TopSection";
import { saveVatOrdersSetting } from "../../../../../stores/appUserDefaultStore";

// 'addons' = system-managed tab showing CHOICE-typed analyses used by BYO dishes.
export type TabId = "all" | "food" | "drinks" | "addons";

interface Tab {
    id: TabId;
    label: string;
}

interface HeaderProps {
    activeTab: TabId;
    onTabChange: (tab: TabId) => void;
    showExVatOrders: boolean;
    onCreateCustomItem: () => void;
    onOpenAutomation?: () => void;
    onOpenNewMenuAutomation?: () => void;
    // Opens the bulk "link custom ingredients" slide-over. Its dry-run spends AI budget,
    // so it inherits the same `cost_price_automation` gate as onOpenAutomation.
    onOpenCustomLink?: () => void;
    onOpenAlternativesLink?: () => void;
    onOpenDelete?: () => void;
    // Export the rows currently visible in the table (same filters/search).
    onExportCsv?: () => void;
    // Same rows, plus each dish's ingredients / subrecipes / add-ons underneath it.
    onExportDetailedCsv?: () => void;
    onCopyToClipboard?: () => Promise<boolean> | boolean;
    showAutomationButton?: boolean;
    showCustomLinkButton?: boolean;
    showDeleteButton?: boolean;
    // Export/copy sit behind the same permission as the automation actions.
    showExportOptions?: boolean;
    // Show the system-managed "Add-ons" tab. Only true when at least one
    // CHOICE analysis exists in the restaurant.
    showAddOnsTab?: boolean;
}

const Header: React.FC<HeaderProps> = ({ activeTab, onTabChange, showExVatOrders, onCreateCustomItem, onOpenAutomation, onOpenNewMenuAutomation, onOpenCustomLink, onOpenAlternativesLink, onOpenDelete, onExportCsv, onExportDetailedCsv, onCopyToClipboard, showAutomationButton, showCustomLinkButton, showDeleteButton, showExportOptions, showAddOnsTab }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [isVatOpen, setIsVatOpen] = useState(false);
    const vatDropdownRef = useRef<HTMLDivElement | null>(null);
    const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false);
    const actionsMenuRef = useRef<HTMLDivElement | null>(null);
    const [justCopied, setJustCopied] = useState(false);

    // The kebab only renders when the user can reach at least one action.
    const canOpenAutomation = !!(showAutomationButton && onOpenAutomation);
    const canLinkCustom = !!(showCustomLinkButton && onOpenCustomLink);
    const canLinkAlternatives = !!(showAutomationButton && onOpenAlternativesLink);
    const canOpenDelete = !!(showDeleteButton && onOpenDelete);
    const canExport = !!(showExportOptions && (onExportCsv || onExportDetailedCsv || onCopyToClipboard));
    const showActionsMenu = canOpenAutomation || canLinkCustom || canLinkAlternatives || canOpenDelete || canExport;

    const tabs: Tab[] = [
        { id: "all", label: t('overallType.all') || "All" },
        { id: "food", label: t('overallType.food') || "Food" },
        { id: "drinks", label: t('overallType.drinks') || "Drinks" },
        // System-managed: only present when the restaurant has any CHOICE analysis.
        ...(showAddOnsTab ? [{ id: "addons" as TabId, label: t('costPrice.addOnsTab') }] : []),
    ];

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (vatDropdownRef.current && !vatDropdownRef.current.contains(event.target as Node)) {
                setIsVatOpen(false);
            }
        };

        if (isVatOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isVatOpen]);

    // Close the actions kebab on outside click or Escape.
    useEffect(() => {
        if (!isActionsMenuOpen) return;
        const handleClickOutside = (event: MouseEvent) => {
            if (actionsMenuRef.current && !actionsMenuRef.current.contains(event.target as Node)) {
                setIsActionsMenuOpen(false);
            }
        };
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") setIsActionsMenuOpen(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [isActionsMenuOpen]);

    // Reset the "Copied" confirmation shortly after it shows.
    useEffect(() => {
        if (!justCopied) return;
        const timer = setTimeout(() => setJustCopied(false), 2000);
        return () => clearTimeout(timer);
    }, [justCopied]);

    const handleCopy = async () => {
        if (!onCopyToClipboard) return;
        const success = await onCopyToClipboard();
        if (success) setJustCopied(true);
    };

    // Handler for VAT toggle
    const handleVatChange = async (value: string) => {
        const showExVat = value === 'excl';
        try {
            await saveVatOrdersSetting(showExVat);
        } catch (error) {
            console.error('Failed to save VAT settings:', error);
        }
    };

    return (
        <div className="w-full flex flex-col gap-4 min-[1250px]:flex-row min-[1250px]:justify-between min-[1250px]:items-center relative pb-6">
            {/* Top row: Title and Add New button (visible when < 1250px) */}
            <div className="flex items-center justify-between min-[1250px]:justify-start">
                <h2 className={cn(xxLargeTextSize, semiBoldText, primaryText, "hidden lg:block")}>
                    {t('costPrice.title')}
                </h2>
                
                {/* Add new button - visible when < 1250px, hidden on large screens (shown in actions below) */}
                <button
                    type="button"
                    onClick={onCreateCustomItem}
                    className={cn(
                        "min-[1250px]:hidden flex items-center justify-center gap-2 text-nowrap rounded-xl px-[13.5px] h-11 text-sm font-semibold bg-[#0E8965] dark:bg-darkGreenColor-dark text-white dark:text-white hover:bg-[#047857] dark:hover:bg-darkGreenColor-dark/90 transition-colors flex-shrink-0",
                    )}
                >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10 4.16699V15.8337" stroke="white" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M4.1665 10H15.8332" stroke="white" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span className="text-white dark:text-white text-[16px] leading-6 font-semibold">{t('costPrice.addNew')}</span>
                </button>
            </div>

            {/* Actions */}
            <div className="flex w-full min-[1250px]:w-fit flex-col gap-3 min-[1050px]:flex-row min-[1050px]:flex-nowrap min-[1050px]:items-center">
                {/* All / Food / Drinks tabs - full width on own row when < 1050px */}
                <div className="inline-flex h-[44px] w-full min-[1050px]:w-auto items-stretch rounded-xl border border-[#D0D5DD] dark:border-borderPrimary-dark bg-white dark:bg-background-dark overflow-hidden flex-shrink-0">
                    {tabs.map((tab, index) => (
                        <button
                            key={tab.id}
                            type="button"
                            className={cn(
                                "flex-1 min-[1050px]:flex-initial flex h-full items-center justify-center px-4 transition-colors text-[14px] leading-5 font-semibold min-w-[80px]",
                                index > 0 && "border-l border-[#E5E7EB] dark:border-borderPrimary-dark",
                                activeTab === tab.id
                                    ? "bg-[#EDFCF5] dark:bg-[#0E8965]/20 text-[#0E8965] dark:text-darkGreenColor-dark"
                                    : "text-[#344054] dark:text-textSecondary-dark hover:bg-[#F9FAFB] dark:hover:bg-backgroundDarker-dark"
                            )}
                            onClick={() => onTabChange(tab.id)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Manage ingredients and VAT toggle - side by side on their own row when < 1050px */}
                <div className="flex w-full min-[1050px]:w-auto flex-row gap-3 items-center">
                    {/* Manage ingredients */}
                    <button
                        onClick={() => navigate('/costprice/manage-ingredients')}
                        className="flex flex-1 min-[1050px]:flex-none min-[1050px]:w-auto items-center justify-center gap-2 rounded-xl border border-[#D0D5DD] dark:border-borderPrimary-dark bg-white dark:bg-background-dark px-3 py-[11px] flex-shrink-0 hover:bg-[#F9FAFB] dark:hover:bg-backgroundDarker-dark transition-colors"
                    >
                        <img src="/assets/calendar.svg" alt="Manage ingredients" width={20} height={20} />
                        <span className="text-[#344054] dark:text-textSecondary-dark text-[14px] leading-5 font-semibold text-nowrap">
                            {t('costPrice.manageIngredientsText')}
                        </span>
                    </button>

                    {/* VAT dropdown */}
                    <div ref={vatDropdownRef} className="relative flex-1 min-[1050px]:flex-none min-[1050px]:w-auto text-nowrap flex-shrink-0">
                        <button
                            type="button"
                            onClick={() => setIsVatOpen((prev) => !prev)}
                            className="inline-flex w-full min-[1050px]:w-auto items-center justify-between gap-1.5 rounded-xl h-11 border border-[#D0D5DD] dark:border-borderPrimary-dark bg-white dark:bg-background-dark px-3 min-w-[130px]"
                        >
                            <span className="text-[#344054] dark:text-textSecondary-dark text-[14px] leading-5 font-semibold">
                                {showExVatOrders ? 'Excl. VAT' : 'Incl. VAT'}
                            </span>

                            <span className={cn("transition-transform", isVatOpen ? "rotate-180" : "rotate-0")}
                                aria-hidden="true"
                            >
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#000000] dark:text-textSecondary-dark" />
                                </svg>
                            </span>
                        </button>

                        {isVatOpen && (
                            <div className="absolute right-0 mt-1 w-full min-[1050px]:w-36 rounded-xl border border-[#E5E7EB] dark:border-borderPrimary-dark bg-white dark:bg-background-dark shadow-lg z-10 py-1">
                                {vatOptions.map((option) => (
                                    <button
                                        key={option.value}
                                        type="button"
                                        className="block w-full px-3 py-2 text-left text-[14px] leading-5 font-medium text-[#111827] dark:text-textPrimary-dark hover:bg-[#F9FAFB] dark:hover:bg-backgroundDarker-dark"
                                        onClick={() => {
                                            handleVatChange(option.value);
                                            setIsVatOpen(false);
                                        }}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Automation + Delete actions collapsed into a kebab menu so they
                    don't overflow the row - visible on large screens only (≥ 1250px). */}
                {showActionsMenu && (
                    <div ref={actionsMenuRef} className="relative hidden min-[1250px]:block flex-shrink-0">
                        <button
                            type="button"
                            onClick={() => setIsActionsMenuOpen(prev => !prev)}
                            aria-haspopup="menu"
                            aria-expanded={isActionsMenuOpen}
                            aria-label={t('costPrice.moreActions')}
                            className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#D0D5DD] dark:border-borderPrimary-dark bg-white dark:bg-background-dark text-[#344054] dark:text-textSecondary-dark hover:bg-[#F9FAFB] dark:hover:bg-backgroundDarker-dark transition-colors"
                        >
                            <MoreVertical className="w-5 h-5" />
                        </button>

                        {isActionsMenuOpen && (
                            <div
                                role="menu"
                                className="absolute right-0 top-full mt-1 min-w-[200px] rounded-xl border border-[#E5E7EB] dark:border-borderPrimary-dark bg-white dark:bg-background-dark shadow-lg py-1 z-50"
                            >
                                {canExport && onExportCsv && (
                                    <button
                                        type="button"
                                        role="menuitem"
                                        onClick={() => { setIsActionsMenuOpen(false); onExportCsv(); }}
                                        className="w-full flex items-center gap-2 px-3 py-2 text-left text-[14px] leading-5 font-semibold text-[#344054] dark:text-textSecondary-dark hover:bg-[#F9FAFB] dark:hover:bg-backgroundDarker-dark"
                                    >
                                        <Download className="w-5 h-5" />
                                        {t('costPrice.export.exportCsv')}
                                    </button>
                                )}
                                {canExport && onExportDetailedCsv && (
                                    <button
                                        type="button"
                                        role="menuitem"
                                        onClick={() => { setIsActionsMenuOpen(false); onExportDetailedCsv(); }}
                                        className="w-full flex items-center gap-2 px-3 py-2 text-left text-[14px] leading-5 font-semibold text-[#344054] dark:text-textSecondary-dark hover:bg-[#F9FAFB] dark:hover:bg-backgroundDarker-dark"
                                    >
                                        <FileSpreadsheet className="w-5 h-5" />
                                        {t('costPrice.export.exportCsvDetailed')}
                                    </button>
                                )}
                                {canExport && onCopyToClipboard && (
                                    <button
                                        type="button"
                                        role="menuitem"
                                        onClick={handleCopy}
                                        className="w-full flex items-center gap-2 px-3 py-2 text-left text-[14px] leading-5 font-semibold text-[#344054] dark:text-textSecondary-dark hover:bg-[#F9FAFB] dark:hover:bg-backgroundDarker-dark"
                                    >
                                        {justCopied ? <Check className="w-5 h-5 text-[#0E8965] dark:text-darkGreenColor-dark" /> : <ClipboardCopy className="w-5 h-5" />}
                                        {justCopied ? t('costPrice.export.copied') : t('costPrice.export.copyToClipboard')}
                                    </button>
                                )}
                                {canOpenAutomation && (
                                    <button
                                        type="button"
                                        role="menuitem"
                                        onClick={() => { setIsActionsMenuOpen(false); onOpenAutomation!(); }}
                                        className="w-full flex items-center gap-2 px-3 py-2 text-left text-[14px] leading-5 font-semibold text-[#344054] dark:text-textSecondary-dark hover:bg-[#F9FAFB] dark:hover:bg-backgroundDarker-dark"
                                    >
                                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M10 2.5V5" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M10 15V17.5" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M4.10833 4.10834L5.875 5.875" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M14.125 14.125L15.8917 15.8917" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M2.5 10H5" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M15 10H17.5" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M4.10833 15.8917L5.875 14.125" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M14.125 5.875L15.8917 4.10834" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                        {t('costPrice.automation')}
                                    </button>
                                )}
                                {canOpenAutomation && onOpenNewMenuAutomation && (
                                    <button
                                        type="button"
                                        role="menuitem"
                                        onClick={() => { setIsActionsMenuOpen(false); onOpenNewMenuAutomation(); }}
                                        className="w-full flex items-center gap-2 px-3 py-2 text-left text-[14px] leading-5 font-semibold text-[#344054] dark:text-textSecondary-dark hover:bg-[#F9FAFB] dark:hover:bg-backgroundDarker-dark"
                                    >
                                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M10 2.5V5" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M10 15V17.5" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M4.10833 4.10834L5.875 5.875" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M14.125 14.125L15.8917 15.8917" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M2.5 10H5" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M15 10H17.5" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M4.10833 15.8917L5.875 14.125" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M14.125 5.875L15.8917 4.10834" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                        {t('costPrice.newMenuAutomation.entry')}
                                    </button>
                                )}
                                {canLinkCustom && (
                                    <button
                                        type="button"
                                        role="menuitem"
                                        onClick={() => { setIsActionsMenuOpen(false); onOpenCustomLink!(); }}
                                        className="w-full flex items-center gap-2 px-3 py-2 text-left text-[14px] leading-5 font-semibold text-[#344054] dark:text-textSecondary-dark hover:bg-[#F9FAFB] dark:hover:bg-backgroundDarker-dark"
                                    >
                                        <Link2 className="w-5 h-5" />
                                        {t('costPrice.customLink.bulkMenuItem')}
                                    </button>
                                )}
                                {canLinkAlternatives && (
                                    <button
                                        type="button"
                                        role="menuitem"
                                        onClick={() => { setIsActionsMenuOpen(false); onOpenAlternativesLink!(); }}
                                        className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm font-semibold text-textSecondary dark:text-textSecondary-dark hover:bg-gray-50 dark:hover:bg-backgroundDarker-dark"
                                    >
                                        <Link2 className="w-5 h-5" />
                                        {t('costPrice.alternativeLink.title')}
                                    </button>
                                )}
                                {canOpenDelete && (
                                    <button
                                        type="button"
                                        role="menuitem"
                                        onClick={() => { setIsActionsMenuOpen(false); onOpenDelete!(); }}
                                        className="w-full flex items-center gap-2 px-3 py-2 text-left text-[14px] leading-5 font-semibold text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                                    >
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
                                        </svg>
                                        {t('costPrice.deleteModal.title')}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Add new - visible on large screens only (≥ 1250px) */}
                <button
                    type="button"
                    onClick={onCreateCustomItem}
                    className={cn(
                        "hidden min-[1250px]:flex items-center justify-center gap-2 text-nowrap rounded-xl px-[13.5px] h-11 text-sm font-semibold bg-[#0E8965] dark:bg-darkGreenColor-dark text-white dark:text-white hover:bg-[#047857] dark:hover:bg-darkGreenColor-dark/90 transition-colors flex-shrink-0 min-[1250px]:ml-auto",
                    )}
                >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10 4.16699V15.8337" stroke="white" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M4.1665 10H15.8332" stroke="white" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span className="text-white dark:text-white text-[16px] leading-6 font-semibold">{t('costPrice.addNew')}</span>
                </button>
            </div>
        </div>
    );
};

export default Header;